import { PaymentMode, ShipmentInterface } from "../interfaces";

/**
 * Payment modes that may be created as a multi-guía batch (lote) from the
 * manual shipment-creation flow. The batch exists so the client signs ONE
 * liability waiver (declaración jurada) covering every guía.
 *
 * Note: DEST_BOXOFFICE_CREDIT (5) and EXEMPT (6) are intentionally excluded
 * here. EXEMPT batches are handled by the dedicated Internal Mail flow.
 * Preshipment batches allow any matching mode — see {@link isBatchEligible}.
 */
export const BATCH_ENABLED_PAYMENT_MODES: PaymentMode[] = [
  PaymentMode.CONTADO,
  PaymentMode.BOXOFFICE_CREDIT,
  PaymentMode.COD,
  PaymentMode.CREDIT,
];

/**
 * True when two drafts can coexist in the same batch (lote): same shipper
 * (identity number + abbreviation), same payment mode, same origin BU.
 *
 *   draft A ──┐
 *             ├─ isSameBatch? ──▶ shipper id + abreviation + paymentMode + buSource.code all equal
 *   draft B ──┘
 *
 * The shared liability waiver (one signer) and any grouped order rely on this
 * invariant. accountBillTo is deliberately NOT compared: for COD it is the
 * per-guía consignee, which legitimately differs across the batch.
 */
export const isSameBatch = (
  a?: Partial<ShipmentInterface>,
  b?: Partial<ShipmentInterface>
): boolean => {
  if (!a || !b) return false;
  return (
    a.shipper?.identificationNumber === b.shipper?.identificationNumber &&
    a.shipper?.abreviationName === b.shipper?.abreviationName &&
    a.paymentMode === b.paymentMode &&
    a.buSource?.code === b.buSource?.code
  );
};

/**
 * True when a batch anchored by `draft0` may grow with "Añadir nueva guía".
 *
 *   draft0 has preShipmentHeaderID  ──▶ preshipment batch: ANY payment mode allowed
 *   otherwise                       ──▶ manual batch: mode must be in BATCH_ENABLED_PAYMENT_MODES
 *
 * The "all drafts must originate from a preshipment" and "last draft already
 * loaded" gates for preshipment batches live in the caller (it needs the full
 * drafts array); this function only judges the batch anchor.
 */
export const isBatchEligible = (
  draft0?: Partial<ShipmentInterface>
): boolean => {
  if (!draft0) return false;
  if (draft0.preShipmentHeaderID) return true;
  return (
    draft0.paymentMode !== undefined &&
    BATCH_ENABLED_PAYMENT_MODES.includes(draft0.paymentMode)
  );
};

/**
 * Strip the lote-invariant fields (payment mode, origin BU, shipper) from a
 * draft update whenever they do not MATCH the anchor guía.
 *
 * Outside a lote (0 or 1 drafts) the update passes through untouched.
 *
 * "Does not match" includes ERASURE, not just divergence. The store merges
 * updates with a spread, so an explicit `paymentMode: undefined` — which
 * "Limpiar Guía" sends — would overwrite the lote's mode with nothing, and the
 * mode selector stays disabled in a lote, leaving no way to restore it. Keys
 * are removed with `delete` (never set to undefined) so the spread preserves
 * whatever the store already holds.
 *
 * Everything else passes through: consignee, address, pieces, and COD's
 * per-guía accountBillTo are legitimately different on each guía.
 */
export const applyBatchInvariants = <T extends Partial<ShipmentInterface>>(
  draft: T,
  drafts: Partial<ShipmentInterface>[]
): T => {
  if (drafts.length <= 1) return draft;

  const anchor = drafts[0];
  const next: T = { ...draft };

  if (next.paymentMode !== anchor?.paymentMode) {
    delete next.paymentMode;
  }
  if (next.buSource?.code !== anchor?.buSource?.code) {
    delete next.buSource;
  }
  if (
    next.shipper?.identificationNumber !==
      anchor?.shipper?.identificationNumber ||
    next.shipper?.abreviationName !== anchor?.shipper?.abreviationName
  ) {
    delete next.shipper;
  }

  return next;
};
